import {
  History,
  Loader2,
  RotateCcw,
  Search,
  SlidersHorizontal,
  StopCircle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { localizeDigits } from '@/lib/localize-digits'

export interface SearchActionBarProps {
  selectedAccountId: string | null
  selectedAccountName: string | undefined
  isSearching: boolean
  activeFiltersCount: number
  isStartPending: boolean
  isStopPending: boolean
  onStartSearch: () => void
  onStopSearch: () => void
  onReset: () => void
  onShowAccountSelection: () => void
  onToggleFilters: () => void
  onNavigateToStatus: () => void
}

export function SearchActionBar({
  selectedAccountId,
  selectedAccountName,
  isSearching,
  activeFiltersCount,
  isStartPending,
  isStopPending,
  onStartSearch,
  onStopSearch,
  onReset,
  onShowAccountSelection,
  onToggleFilters,
  onNavigateToStatus,
}: SearchActionBarProps) {
  const { t } = useTranslation()

  return (
    <>
      {/* Filter Summary & Account Info */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {selectedAccountId ? (
            <Badge variant="default" className="text-xs">
              {selectedAccountName}
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="text-xs cursor-pointer"
              onClick={onShowAccountSelection}
            >
              {t('search.noAccount')}
            </Badge>
          )}
          {isSearching && (
            <Badge
              variant="default"
              className="text-xs bg-yellow-600 animate-pulse"
            >
              <Loader2 className="h-3 w-3 me-1 animate-spin" />
              {t('search.searching') || 'Searching...'}
            </Badge>
          )}
          {activeFiltersCount > 0 && !isSearching && (
            <Badge variant="secondary" className="text-xs">
              {localizeDigits(String(activeFiltersCount))}{' '}
              {activeFiltersCount !== 1
                ? t('search.filters')
                : t('search.filter')}
            </Badge>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          className="flex-shrink-0"
          aria-label="Reset search"
        >
          <RotateCcw className="h-3 w-3 me-1" />
          {t('search.reset')}
        </Button>
      </div>

      {/* Compact Action Row */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onNavigateToStatus}
          className="flex-shrink-0"
          aria-label="Search history"
        >
          <History className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          {isSearching ? (
            <Button
              className="w-full"
              size="sm"
              variant="destructive"
              onClick={onStopSearch}
              disabled={isStopPending}
              aria-label="Stop search"
            >
              {isStopPending ? (
                <Loader2 className="h-4 w-4 me-2 animate-spin" />
              ) : (
                <StopCircle className="h-4 w-4 me-2" />
              )}
              {t('search.stopSearch') || 'Stop Search'}
            </Button>
          ) : selectedAccountId ? (
            <Button
              className="w-full"
              size="sm"
              onClick={onStartSearch}
              disabled={isStartPending}
            >
              <Search className="h-4 w-4 me-2" />
              {t('search.startSearch')}
            </Button>
          ) : (
            <Button
              variant="outline"
              className="w-full"
              size="sm"
              onClick={onShowAccountSelection}
            >
              {t('search.selectAccount')}
            </Button>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onToggleFilters}
          className="flex-shrink-0"
          aria-label="Toggle filters"
        >
          <SlidersHorizontal className="h-4 w-4" />
        </Button>
      </div>
    </>
  )
}
